<template>
  <div class="row">
    <div ref="products" class="routine-products">
      <carousel
        :per-page="perPage"
        :carousel-identifier="carouselIdentifier"
        :navigation-enabled="enableNavigation"
        @pageChange="pageChange"
      >
        <slide v-for="(slot, index) in getSlots()" :key="index">
          <slot :name="`product${slot}`"></slot>
        </slide>
      </carousel>
    </div>
  </div>
</template>

<script>
const DEFAULT_PER_PAGE = 1;

import throttle from 'lodash/throttle';

export default {
  name: 'augment-product-carousel',
  data() {
    return {
      perPage: this.getPerPage(),
      slides: false,
      currentSlide: 0,
      taggingInfo: [],
      scrollCallbackReference: null,
      eventTriggeredItems: [],
    };
  },

  props: {
    carouselIdentifier: { type: String, required: false, default: `carousel-${Date.now()}` },
    enableNavigation: { type: Boolean, default: false },
    moduleTitle: { type: String, required: false },
  },

  mounted() {
    this.perPage = this.getPerPage();
    this.slides = this.$el.querySelectorAll('.carousel-slide');
    if (window.eventBus) {
      window.eventBus.$on('mediaquery.changed', () => {
        this.perPage = this.getPerPage();
        this.fireProductImpression(this.currentSlide);
      });
      window.eventBus.$on(`${this.carouselIdentifier}-pageChange`, this.onPageChange);
    }
    this.scrollCallbackReference = throttle(() => {
      this.fireProductImpression(this.currentSlide);
    }, 500);

    /* instanbul ignore next */
    window.addEventListener('scroll', this.scrollCallbackReference);
    this.removelozyloaded(this.perPage);
    window.scrollBy(0, 2);
  },

  methods: {
    pageChange(value) {
      this.$emit('updateProduct', value);
    },
    getSlots() {
      return Object.keys(this.$slots).length;
    },
    getPerPage() {
      let perPage = DEFAULT_PER_PAGE;
      /* istanbul ignore next */
      if (window.ScreenSizeHandler) {
        if (window.ScreenSizeHandler.getMqForSize('small').selected) {
          perPage = 1;
        }

        if (window.ScreenSizeHandler.getMqForSize('medium').selected) {
          perPage = 2;
        }

        if (window.ScreenSizeHandler.getMqForSize('large').selected) {
          perPage = 3;
        }
      }
      window.eventBus.$emit('perPage', perPage);
      return perPage;
    },
    fireProductImpression(currentSlide) {
      if (!this.isElementInViewport(this.$refs.products)) {
        return;
      }
      this.currentSlide = currentSlide;
      if (this.perPage === 1) {
        const param = this.slides[this.currentSlide];
        this.taggingInfo = this.getTaggingInfoForSlides(param);
      }

      if (this.perPage === 2) {
        const param1 = this.slides[this.currentSlide];
        const param2 = this.slides[this.currentSlide + 1];
        this.taggingInfo = this.getTaggingInfoForSlides([param1, param2]);
      }

      if (this.perPage === 3) {
        const param1 = this.slides[this.currentSlide];
        const param2 = this.slides[this.currentSlide + 1];
        const param3 = this.slides[this.currentSlide + 2];
        this.taggingInfo = this.getTaggingInfoForSlides([param1, param2, param3]);
      }

      if (this.scrollCallbackReference) {
        window.removeEventListener('scroll', this.scrollCallbackReference);
      }
      this.triggerTaggingEvent(this.taggingInfo);
    },
    /// PRODUCT IMPRESSIONS - AUGMENTED PRODUCTS
    triggerTaggingEvent(taggingItems) {
      // NOSONAR
      const impressions = [];
      const products = [];
      let productTaggingDetails;
      taggingItems.forEach((taggingItem) => {
        const { ecommerce } = taggingItem;
        if (ecommerce && ecommerce.impressions && ecommerce.impressions.length) {
          ecommerce.impressions.forEach((el) => {
            el['dimension35'] = el['dimension35'].split(' ')[0];
            el['variant'] = el['variant'].split(' ')[0];
          });
          productTaggingDetails = ecommerce.impressions[0];
          impressions.push({
            name: productTaggingDetails.name,
            id: productTaggingDetails.id,
            price: productTaggingDetails.price,
            brand: productTaggingDetails.brand,
            category: productTaggingDetails.category,
            list: productTaggingDetails.list,
            position: productTaggingDetails.position,
            dimension35: productTaggingDetails.dimension35,
            dimension38: productTaggingDetails.dimension38,
            dimension48: productTaggingDetails.dimension48,
            dimension91: productTaggingDetails.dimension91,
          });
        } else if (ecommerce && ecommerce.products && ecommerce.products.length) {
          ecommerce.products.forEach((el) => {
            el['dimension35'] = el['dimension35'].split(' ')[0];
            el['variant'] = el['variant'].split(' ')[0];
          });
          productTaggingDetails = ecommerce.impressions[0];
          products.push({
            name: productTaggingDetails.name,
            id: productTaggingDetails.id,
            price: productTaggingDetails.price,
            brand: productTaggingDetails.brand,
            category: productTaggingDetails.category,
            list: productTaggingDetails.list,
            position: productTaggingDetails.position,
            dimension35: productTaggingDetails.dimension35,
            dimension38: productTaggingDetails.dimension38,
            dimension48: productTaggingDetails.dimension48,
            dimension91: productTaggingDetails.dimension91,
          });
        }
      });
      const { event, eventLabel, eventAction, eventCategory, ecommerce } = taggingItems[0];
      const { currencyCode } = ecommerce;
      try {
        if (impressions && impressions.length) {
          if (eventAction == 'Product Impressions') {
            dataLayer.push({
              event: event,
              event_name: 'view_item_list',
              eventCategory: eventCategory,
              eventAction: eventAction,
              eventLabel: eventLabel,
              ecommerce: {
                currencyCode: currencyCode,
                impressions: impressions,
                products: products,
              },
            });
          }
          /// Augmented Slider tagging
          dataLayer.push({
            event: 'uaevent',
            ecommerce: 'undefined',
            eventCategory: 'augmented products',
            eventAction: `select::carousel::${!this.moduleTitle ? 'recommended routine' : this.moduleTitle.toLowerCase()}`,
            eventLabel: `${impressions[0].position}::${impressions[0].name.toLowerCase()}::${impressions[0].id}`,
          });
        } else if (products && products.length) {
          if (eventAction == 'Product Impressions') {
            dataLayer.push({
              event: event,
              event_name: 'view_item_list',
              eventCategory: eventCategory,
              eventAction: eventAction,
              eventLabel: eventLabel,
              ecommerce: {
                currencyCode: currencyCode,
                impressions: impressions,
                products: products,
              },
            });
          }
          /// Augmented Slider tagging
          dataLayer.push({
            event: 'uaevent',
            ecommerce: 'undefined',
            eventCategory: 'augmented products',
            eventAction: `select::carousel::${!this.moduleTitle ? 'recommended routine' : this.moduleTitle.toLowerCase()}`,
            eventLabel: `${products[0].position}::${products[0].name.toLowerCase()}::${products[0].id}`,
          });
        }
      } catch {
        console.warn('datalayer is not defined');
      }
    },
    getTaggingInfoForSlides(nodesAsArray) {
      const data = [];
      if (this.perPage === 1 && nodesAsArray) {
        data.push(this.getTaggingDataForNode(nodesAsArray));
      } else {
        nodesAsArray.forEach((node) => {
          if (node) {
            data.push(this.getTaggingDataForNode(node));
          }
        });
      }
      return data;
    },

    getTaggingDataForNode(node) {
      const taggingData = node
        .querySelector('[data-tagging-on-impression]')
        .getAttribute('data-tagging-on-impression');
      return JSON.parse(taggingData) || false;
    },

    onPageChange(currentSlide) {
      /* istanbul ignore next */
      this.fireProductImpression(currentSlide);
      window.eventBus.$emit('fireOnClick', currentSlide);
    },

    isElementInViewport(element) {
      const rect = element.getBoundingClientRect();
      const elementCenter = {
        x: rect.left + element.offsetWidth / 2,
        y: rect.top + element.offsetHeight / 2,
      };
      let pointContainer = document.elementFromPoint(elementCenter.x, elementCenter.y);
      if (
        elementCenter.x < 0 ||
        elementCenter.y < 0 ||
        elementCenter.x > (document.documentElement.clientWidth || window.innerWidth) ||
        elementCenter.y > (document.documentElement.clientHeight || window.innerHeight)
      ) {
        return false;
      }

      do {
        if (pointContainer === element) {
          return true;
        }
      } while ((pointContainer = pointContainer.parentNode));

      return false;
    },
    removelozyloaded(perPage) {
      // remove lazyloaded class
      const a = document.getElementsByClassName('routine-products')[0];
      // a.classList.add('slide')
      const b = a.querySelectorAll('.carousel-slide img');
      if (b.length > perPage) {
        for (let i = perPage; i < b.length; i++) {
          b[i].classList.remove('lazyload');
        }
      }
      //  end
    },
  },
};
</script>
